import {
    removeFromDom,
    getLocalHistory,
    saveLocalHistory,
    sanitizeString,
    sendGaSearchEvent,
    elementValues,
    keys,
} from './utils';
import { LocalHistoryEntry } from './types';

export const searchBoxFactory = (browserStorageKey: string, maxSuggestion: number) => ({
    browseList: false,
    focusOnInput: false,
    inputFieldData: '',
    suggestionList: [] as LocalHistoryEntry[],
    suggestionFromLocalStorage: false,
    selectedIndex: 0,
    browserStorageKey,
    maxSuggestion,

    initSearchboxState() {
        this.inputFieldData = this.$refs.inputFieldRef.value;
    },

    getSanitizedValue(value: string) {
        return sanitizeString(value);
    },

    /**
     * Dispatch an action to HTMX trigger feature.
     *
     */
    getSearchResult() {
        this.$dispatch('dispatchActionSearch');
    },

    addQueryStringToUrl(keyword: string) {
        window.location.href = '/s/' + encodeURIComponent(keyword) + '/';
    },

    submitHandler(keyword: string) {
        const cleanKeyword = this.getSanitizedValue(keyword);

        if (cleanKeyword.length > 1) {
            this.addQueryStringToUrl(cleanKeyword);

            if (!this.suggestionFromLocalStorage) {
                saveLocalHistory(cleanKeyword, browserStorageKey, maxSuggestion);
            }

            sendGaSearchEvent(cleanKeyword);
        }
    },

    keyupHandler(ev: KeyboardEvent) {
        if (ev.key === keys.ESC || ev.key === keys.TAB) {
            this.resetInputField();
            return;
        }

        this.inputFieldData = this.$refs.inputFieldRef.value;

        if (ev.key !== keys.UP && ev.key !== keys.DOWN && this.browseList) {
            this.selectedIndex = 0;
            this.browseList = false;
        }
    },

    /**
     * This should be set the component main state accordingly to user input.
     *
     */
    keyUpHandler() {
        if (!this.focusOnInput) return;

        if (this.inputFieldData === '') {
            this.suggestionFromLocalStorage = true;
            removeFromDom('search-results');
            this.suggestionList = getLocalHistory(browserStorageKey);
        } else {
            if (!this.browseList) {
                this.suggestionList = [];
                this.suggestionFromLocalStorage = false;
                this.getSearchResult();
            }
        }
    },

    getSelected(elements: LocalHistoryEntry[] | HTMLCollection, index: number, up = false) {
        if (!elements?.length) return 0;

        if (up) {
            return index === 1 || index === 0 ? elements.length : index - 1;
        } else {
            return (index % elements.length) + 1;
        }
    },

    getInputValue(list: any, index: number, val: string) {
        if (!list?.length) return '';

        return list[index - 1][val];
    },

    /**
     * Handles up and down arrow, and set local variables when user browing the suggestion list.
     *
     */
    browseHandler(upDirection = false) {
        const searchResults = document.getElementById('search-results');
        if (!searchResults) return;
        const searchResult = searchResults.getElementsByTagName('li');

        if (this.suggestionList.length || searchResult.length) {
            this.browseList = true;

            if (this.suggestionList.length) {
                this.suggestionBrowseHandler(upDirection);
            }

            if (searchResult.length) {
                this.searchResultBrowseHandler(searchResult, upDirection);
            }
        }
    },

    /**
     * Return selected item index from suggestion list.
     *
     */
    suggestionBrowseHandler(upDirection: boolean) {
        this.selectedIndex = this.getSelected(this.suggestionList, this.selectedIndex, upDirection);
        this.$refs.inputFieldRef.value = this.getInputValue(
            this.suggestionList,
            this.selectedIndex,
            elementValues.VALUE
        );
    },

    /**
     * Return selected item index from search result.
     *
     */
    searchResultBrowseHandler(searchResult: HTMLCollection, upDirection: boolean) {
        this.selectedIndex = this.getSelected(searchResult, this.selectedIndex, upDirection);
        this.$refs.inputFieldRef.value =
            this.getInputValue(searchResult, this.selectedIndex, elementValues.INNERTEXT);
    },

    /**
     * Remove the focus from input and perform reset function.
     *
     */
    resetInputField() {
        this.$refs.inputFieldRef?.blur();
        this.reset();
    },

    /**
     * Set the component state and UI to starting position.
     * Remove search result if any.
     *
     */
    reset() {
        this.focusOnInput = false;
        this.suggestionList = [];
        this.suggestionFromLocalStorage = false;
        this.browseList = false;
        this.selectedIndex = 0;
        removeFromDom('search-results');
    }
});
